import React, { useCallback, useMemo, useState } from 'react';
import {
	Box,
	CircularProgress,
	FormControl,
	FormLabel,
	Stack,
	Switch,
	useTheme,
} from '@mui/material';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer } from '@mui/x-data-grid';
import Fuse from 'fuse.js';
import { doc, updateDoc } from 'firebase/firestore';
import { useDispatch } from 'react-redux';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import SearchField from '@/components/SearchField';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { firestore, toBrokerGroupRefs } from '@/utils/firebase';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUsersByIdsQuery } from '@/features/user/userApi';
import { ADMIN_MODE_FEATURE_FLAG } from '@/hooks/useAdminModeAccess';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { columns as dataColumns } from './data';
import { FEATURE_FLAGS_FUSE_OPTIONS, featureFlagUserLabel } from './helpers';
import { FeatureFlagAccessDialog } from './FeatureFlagAccessDialog';

const EMPTY = [];

const ColumnsToolbar = () => (
	<GridToolbarContainer>
		<GridToolbarColumnsButton />
	</GridToolbarContainer>
);

export const FeatureFlagsTable = () => (
	<ColumnControlContextProvider variant='featureFlags' value='list'>
		<FeatureFlagsTableContent />
	</ColumnControlContextProvider>
);

const FeatureFlagsTableContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [searchValue, setSearchValue] = useState('');
	const [updatingIds, setUpdatingIds] = useState([]);
	const [editing, setEditing] = useState(null);
	const [savingAccess, setSavingAccess] = useState(false);
	const [pendingToggle, setPendingToggle] = useState(null);
	const { featureFlags = EMPTY, isLoading, isError } = useFeatureFlagContext();

	const flagUserIds = useMemo(() => {
		const ids = new Set();
		featureFlags.forEach((flag) => {
			(flag.userIds ?? []).forEach((id) => {
				if (id) ids.add(id);
			});
		});
		return Array.from(ids);
	}, [featureFlags]);
	const { data: flagUsers = EMPTY } = useGetUsersByIdsQuery(flagUserIds, {
		skip: flagUserIds.length === 0,
	});

	const usersById = useMemo(() => {
		const byId = new Map();
		flagUsers.forEach((user) => byId.set(user.id, user));
		return byId;
	}, [flagUsers]);

	const rows = useMemo(
		() =>
			(featureFlags ?? [])
				.filter((flag) => flag?.id)
				.map((flag) => {
					const userIds = flag.userIds ?? [];
					return {
						id: flag.id,
						feature: flag.feature ?? '-',
						description: flag.description ?? '',
						active: Boolean(flag.active),
						userIds,
						brokerGroupIds: flag.brokerGroupIds ?? [],
						users: userIds.map((id) => {
							const user = usersById.get(id);
							return {
								id,
								email: user?.email,
								name: featureFlagUserLabel(user),
							};
						}),
						createdWhen: flag.createdWhen ?? null,
					};
				}),
		[featureFlags, usersById],
	);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FEATURE_FLAGS_FUSE_OPTIONS.keys, rows);
		return new Fuse(rows, FEATURE_FLAGS_FUSE_OPTIONS, fuseIndex);
	}, [rows]);

	const filteredRows = useMemo(() => {
		if (!searchValue.trim()) return rows;
		return searchFuse.search(searchValue).map((result) => result.item);
	}, [rows, searchFuse, searchValue]);

	const handleToggle = useCallback(
		async (id, active) => {
			if (updatingIds.includes(id)) return;

			setUpdatingIds((current) => [...current, id]);
			try {
				await updateDoc(doc(firestore, 'featureFlags', id), { active });
			} catch (error) {
				dispatch(
					showToast({
						message: error?.message ?? 'Failed to update feature flag',
						error: true,
					}),
				);
			} finally {
				setUpdatingIds((current) => current.filter((item) => item !== id));
			}
		},
		[dispatch, updatingIds],
	);

	const handleSaveAccess = useCallback(
		async (userIds, brokerGroupIds, description) => {
			if (!editing?.id) return;
			setSavingAccess(true);
			try {
				await updateDoc(doc(firestore, 'featureFlags', editing.id), {
					description: description ?? '',
					users: userIds.map((id) => doc(firestore, 'user', id)),
					brokerGroups: toBrokerGroupRefs(brokerGroupIds),
				});
				setEditing(null);
			} catch (error) {
				dispatch(
					showToast({
						message: error?.message ?? 'Failed to update feature flag',
						error: true,
					}),
				);
			} finally {
				setSavingAccess(false);
			}
		},
		[dispatch, editing],
	);

	const tableColumns = useMemo(
		() =>
			dataColumns.map((column) =>
				column.field === 'active'
					? {
							...column,
							renderCell: (params) => (
								<Switch
									size='small'
									checked={Boolean(params.row.active)}
									disabled={updatingIds.includes(params.row.id)}
									onClick={(event) => event.stopPropagation()}
									onChange={(event) => {
										const active = event.target.checked;
										if (params.row.feature === ADMIN_MODE_FEATURE_FLAG) {
											setPendingToggle({ id: params.row.id, active });
											return;
										}
										handleToggle(params.row.id, active);
									}}
									slotProps={{
										input: { 'aria-label': `${params.row.feature} status` },
									}}
								/>
							),
						}
					: column,
			),
		[handleToggle, updatingIds],
	);

	const { columns, columnVisibilityModel, onColumnVisibilityModelChange, onColumnWidthChange } =
		useDataGridColumnProps(tableColumns);

	if (isLoading && rows.length === 0) {
		return <CircularProgress />;
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', sm: 'flex-end' }}
				sx={{ mb: 2 }}
			>
				<FormControl sx={{ width: { xs: '100%', sm: 320 } }}>
					<FormLabel sx={{ mb: 0.5, lineHeight: '1.5', display: 'block' }}>
						Search
					</FormLabel>
					<SearchField
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						onReset={() => setSearchValue('')}
						helperText=''
					/>
				</FormControl>
			</Stack>

			<Box
				sx={{
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<DataGrid
					initialState={{
						sorting: { sortModel: [{ field: 'feature', sort: 'asc' }] },
					}}
					loading={isLoading}
					rows={filteredRows}
					columns={columns}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={onColumnVisibilityModelChange}
					onColumnWidthChange={onColumnWidthChange}
					disableRowSelectionOnClick
					hideFooterPagination
					hideFooter
					disableColumnFilter
					onRowClick={(params, event) => {
						if (event.target.closest('input, .MuiSwitch-root, button')) return;
						setEditing(params.row);
					}}
					onCellKeyDown={(params, event) => {
						if (!['Enter', ' '].includes(event.key)) return;
						if (event.target.closest('input, .MuiSwitch-root, button')) return;
						event.preventDefault();
						setEditing(params.row);
					}}
					sx={{
						border: 0,
						borderRadius: '8px',
						'& .MuiDataGrid-cell': { outline: 'none' },
						'& .MuiDataGrid-row': { cursor: 'pointer' },
						'--DataGrid-overlayHeight': '3em',
					}}
					slots={{
						noRowsOverlay: isError
							? () => <NoRowsErrorOverlay objectType='feature flags' />
							: () => <NoRowsOverlay objectType='feature flags' />,
						noResultsOverlay: isError
							? () => <NoRowsErrorOverlay objectType='feature flags' />
							: () => <NoRowsOverlay objectType='feature flags' />,
						toolbar: ColumnsToolbar,
						columnMenu: DataGridColumnMenu,
					}}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
					}}
				/>
			</Box>
			<FeatureFlagAccessDialog
				flag={editing}
				saving={savingAccess}
				onClose={() => setEditing(null)}
				onSave={handleSaveAccess}
			/>
			<ConfirmDialog
				openDialog={Boolean(pendingToggle)}
				handleClose={() => setPendingToggle(null)}
				handleConfirm={() => {
					const next = pendingToggle;
					setPendingToggle(null);
					if (next) handleToggle(next.id, next.active);
				}}
				title='Change Administration Mode?'
				content='This updates adminModeEnabled for everyone. If this account is not otherwise targeted by the flag, turning it off removes access to this page.'
				confirmText='Update flag'
			/>
		</Box>
	);
};
